import React, { FC, ReactNode } from 'react'

import { LinariaClassName, css, cx } from '@linaria/core'
import { styled } from '@linaria/react'

import { WithChildren } from 'common/types'
import { fullStopPropagation } from 'functions/fullStopPropagation'

import { TipSvg } from './svg/TipSvg'
import { Position } from './types'
import { zIndex } from '../zIndex'

export const TooltipWithPinBase: FC<
  {
    /**
     * По-умолчанию работает по hover.
     * @show для ручного управления показом.
     */
    show?: boolean
    background?: Background
    content: ReactNode
    position: Position
    /** Если нужно скорректировать позиционирование по вертикали */
    shiftY?: number
  } & WithChildren
> = ({
  show,
  content,
  background = 'light',
  position,
  shiftY = 0,
  children,
}) => {
  return (
    <Container
      className={cx(
        POSITIONS_CSS_MAP[position],
        typeof show === 'boolean' ? show && showCss : showWhenHoverCss
      )}
      onClick={fullStopPropagation} // Чтобы при клике на тело тултипа не срабатывал dispatch(hideTooltipsAction()) в MambaLayoutIndex
    >
      {children}
      <Inner shiftY={shiftY}>
        <Content>
          <ContentInner className={BACKGROUND_CSS_MAP[background]}>
            {content}
          </ContentInner>
        </Content>
        <Tip
          className={cx(
            bottomPositions.includes(position) && tipRotateCss,
            TIP_COLOR_CSS_MAP[background]
          )}
        />
      </Inner>
    </Container>
  )
}

const bottomPositions: Position[] = ['bottomLeft', 'bottomRight']

const ContentInner = styled.div`
  border-radius: 20px;
  box-shadow: 0 0 0 4px var(--rose-opacity-hard);
  padding: var(--spacing-5x);
  color: var(--accent-primary-on-accent-primary);
`
const Inner = styled.div<{ shiftY: number }>`
  position: relative;
  z-index: ${zIndex.default};
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.3s;
  transform: translateY(${({ shiftY }) => shiftY}px);
`
const Content = styled.div`
  position: absolute;
`
const Tip = styled(TipSvg)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`
const tipRotateCss = css`
  transform: translateX(-50%) rotate(180deg);
`
const Container = styled.div`
  position: relative;
  display: inline-block;
  font-size: 0;
`
const showWhenHoverCss = css`
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${Inner} {
        visibility: visible;
        opacity: 1;
      }
    }
  }
`
const showCss = css`
  ${Inner} {
    visibility: visible;
    opacity: 1;
  }
`

const DESIGN_SHIFT_PX = 20

const POSITIONS_CSS_MAP: Record<Position, LinariaClassName> = {
  topLeft: css`
    ${Content} {
      bottom: 100%;

      html[dir='ltr'] & {
        right: -${DESIGN_SHIFT_PX}px;
      }

      html[dir='rtl'] & {
        left: -${DESIGN_SHIFT_PX}px;
      }

      padding-bottom: ${DESIGN_SHIFT_PX}px;
    }
    ${Tip} {
      bottom: calc(100% + 4px);
    }
  `,
  topRight: css`
    ${Content} {
      bottom: 100%;

      html[dir='ltr'] & {
        left: -${DESIGN_SHIFT_PX}px;
      }

      html[dir='rtl'] & {
        right: -${DESIGN_SHIFT_PX}px;
      }

      padding-bottom: ${DESIGN_SHIFT_PX}px;
    }
    ${Tip} {
      bottom: calc(100% + 4px);
    }
  `,
  bottomLeft: css`
    ${Content} {
      top: 100%;

      html[dir='ltr'] & {
        right: -${DESIGN_SHIFT_PX}px;
      }

      html[dir='rtl'] & {
        left: -${DESIGN_SHIFT_PX}px;
      }

      padding-top: ${DESIGN_SHIFT_PX}px;
    }
    ${Tip} {
      top: calc(100% + 4px);
    }
  `,
  bottomRight: css`
    ${Content} {
      top: 100%;

      html[dir='ltr'] & {
        left: -${DESIGN_SHIFT_PX}px;
      }

      html[dir='rtl'] & {
        right: -${DESIGN_SHIFT_PX}px;
      }

      padding-top: ${DESIGN_SHIFT_PX}px;
    }
    ${Tip} {
      top: calc(100% + 4px);
    }
  `,
}

type Background = 'light' | 'dark'

const BACKGROUND_CSS_MAP: Record<Background, LinariaClassName> = {
  light: css`
    background: var(--elevated-bg-elevated-s);
  `,
  dark: css`
    background: var(--accent-primary-default);
  `,
}
const TIP_COLOR_CSS_MAP: Record<Background, LinariaClassName> = {
  light: css`
    color: var(--elevated-bg-elevated-s);
  `,
  dark: css`
    color: var(--accent-primary-default);
  `,
}
